// Deferred-commit helper for optimistic delete with undo. The caller
// invokes `scheduleDelete({ onCommit })` and receives a handle with
// `cancel()` and `flush()`. `onCommit` is the function that actually
// persists the deletion to vault-core; calling `cancel()` before the
// window expires guarantees `onCommit` is never invoked.

export interface DeleteHandle {
  cancel(): void;
  flush(): Promise<void>;
}

interface Options {
  delayMs: number;
  onCommit: () => Promise<void>;
}

export function scheduleDelete({ delayMs, onCommit }: Options): DeleteHandle {
  let cancelled = false;
  let committed = false;
  let timer: number | null = window.setTimeout(() => {
    timer = null;
    if (!cancelled && !committed) {
      committed = true;
      void onCommit();
    }
  }, delayMs);

  const clearTimer = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  };

  return {
    cancel() {
      if (committed) return;
      cancelled = true;
      clearTimer();
    },
    async flush() {
      if (cancelled || committed) return;
      clearTimer();
      committed = true;
      await onCommit();
    },
  };
}
